import { svSE } from '@mui/material/locale';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, createElement } from 'react';
import { Link } from 'react-router-dom';
import { DialogActions, Button } from '@mui/material';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import InfoRounded from '@mui/icons-material/InfoRounded';
import WarningRounded from '@mui/icons-material/WarningRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircle';
import Clear from '@mui/icons-material/Clear';

const fonts = `
/** =================== RALEWAY =================== **/
/** Raleway Bold **/
    @font-face {
        font-family: 'Raleway';
        font-weight: 700;
        font-style: normal;
        src: url('https://static.trr.se/mirror/fonts/Raleway-Bold.eot');
        src: url('https://static.trr.se/mirror/fonts/Raleway-Bold.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/Raleway-Bold.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/Raleway-Bold.woff') format('woff');
    }

    /** Raleway ExtraBold **/
    @font-face {
        font-family: 'Raleway';
        font-weight: 800;
        font-style: normal;
        src: url('https://static.trr.se/mirror/fonts/Raleway-ExtraBold.eot');
        src: url('https://static.trr.se/mirror/fonts/Raleway-ExtraBold.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/Raleway-ExtraBold.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/Raleway-ExtraBold.woff') format('woff');
    }

    /** =================== OPEN SANS =================== **/
    /** Open Sans Regular **/
    @font-face {
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 400;
        src: url('https://static.trr.se/mirror/fonts/OpenSans-Regular.eot');
        src: url('https://static.trr.se/mirror/fonts/OpenSans-Regular.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/OpenSans-Regular.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/OpenSans-Regular.woff') format('woff');
    }

    /** Open Sans Bold **/
    @font-face {
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 500;
        src: url('https://static.trr.se/mirror/fonts/OpenSans-Bold.eot');
        src: url('https://static.trr.se/mirror/fonts/OpenSans-Bold.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/OpenSans-Bold.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/OpenSans-Bold.woff') format('woff');
    }

    /** Open Sans ExtraBold **/
    @font-face {
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 700;
        src: url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBold.eot');
        src: url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBold.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBold.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBold.woff') format('woff');
    }

    /** Open Sans Regular-Italic **/
    @font-face {
        font-family: 'Open Sans';
        font-style: italic;
        font-weight: 400;
        src: url('https://static.trr.se/mirror/fonts/OpenSans-RegularItalic.eot');
        src: url('https://static.trr.se/mirror/fonts/OpenSans-RegularItalic.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/OpenSans-RegularItalic.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/OpenSans-RegularItalic.woff') format('woff');
    }

    /** Open Sans Bold-Italic **/
    @font-face {
        font-family: 'Open Sans';
        font-style: italic;
        font-weight: 500;
        src: url('https://static.trr.se/mirror/fonts/OpenSans-BoldItalic.eot');
        src: url('https://static.trr.se/mirror/fonts/OpenSans-BoldItalic.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/OpenSans-BoldItalic.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/OpenSans-BoldItalic.woff') format('woff');
    }

    /** Open Sans ExtraBold-Italic **/
    @font-face {
        font-family: 'Open Sans';
        font-style: italic;
        font-weight: 700;
        src: url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBoldItalic.eot');
        src: url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBoldItalic.eot?#iefix') format('embedded-opentype'),
            url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBoldItalic.woff2') format('woff2'),
            url('https://static.trr.se/mirror/fonts/OpenSans-ExtraBoldItalic.woff') format('woff');
    }
`;

const general = `
/* =================== General =================== */
*,
*:before,
*:after {
  box-sizing: border-box;
}

html {
  overflow-x: hidden;
  overflow-y: scroll;
}

* {
  font-family: "Open Sans", "Verdana", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  font-weight: 400;
  line-height: 1.5;
  font-size: 1.125rem;
  min-height: 100vh;
  width: 100%;
  position: relative;
}

section {
  margin-bottom: 48px;
}

p {
  margin: 0;
}

h1, h2, h3, h4, h5, h6 {
  font-feature-settings: "lnum", "pnum";
  font-family: "Raleway";
  font-weight: 600;
  line-height: 1.4;
}

h1 { font-size: 2rem; font-weight: 700; line-height: 1.3; }
h2 { font-size: 1.625rem; }
h3 { font-size: 1.375rem; }
h4 { font-size: 1.25rem; }
h5 { font-size: 1.125rem; }
h6 { font-size: 1.025rem; }

@media (min-width: 768px) {
  h1 { font-size: 3rem; }
  h2 { font-size: 2rem; }
  h3 { font-size: 1.625rem; }
  h4 { font-size: 1.375rem; }
  h5, h6 { font-size: 1.125rem; }
}

.lockscroll, .lockscroll > body {
  overflow: hidden;
  height: 100%;
}
`;

const icons = `
/** =================== Icons =================== **/
    @font-face {
        font-family: 'Material Icons';
        font-style: normal;
        font-weight: 400;
        src:
        local('Material Icons'),
        local('MaterialIcons-Regular'),
        url('https://static.trr.se/mirror/fonts/material-icons.woff2')
            format('woff2'),
        url('https://static.trr.se/mirror/fonts/material-icons.woff')
            format('woff');
    }

    .material-icons {
        font-family: 'Material Icons';
        font-weight: normal;
        font-style: normal;
        font-size: 24px; /* Preferred icon size */
        display: inline-block;
        line-height: 1;
        text-transform: none;
        letter-spacing: normal;
        word-wrap: normal;
        white-space: nowrap;
        direction: ltr;

        /* Support for all WebKit browsers. */
        -webkit-font-smoothing: antialiased;
        /* Support for Safari and Chrome. */
        text-rendering: optimizeLegibility;

        /* Support for Firefox. */
        -moz-osx-font-smoothing: grayscale;

        /* Support for IE. */
        font-feature-settings: 'liga';
    } 
`;

var baseLineCss = `${fonts}${icons}${general}`;

var palette$1 = {
	primary: {
		dark: {
			value: "#411782"},
		main: {
			value: "#6222C3"},
		light: {
			value: "#E7D9FC"}
	},
	secondary: {
		dark: {
			value: "#0A5C56"},
		main: {
			value: "#089188"},
		light: {
			value: "#BDF4F0"}
	},
	accent: {
		dark: {
			value: "#E89900"},
		main: {
			value: "#FFC700"},
		light: {
			value: "#FFF8CC"}
	},
	neutralColors: {
		main: {
			value: "#1A1A1A"},
		light: {
			value: "#1A1A1AB2"},
		disabled: {
			value: "#1A1A1A66"},
		divider: {
			value: "#1A1A1A33"}
	},
	stateColors: {
		outlinedChipHover: {
			value: "#0000000A"},
		filledChipHover: {
			value: "#9ECFCD"},
		outlinedButtonHover: {
			value: "#6222C30A"},
		tableRowSelected: {
			value: "#DEF9F7"},
		tableRowHover: {
			value: "#D9F4F2"},
		tabSelected: {
			value: "#6222c31f"}
	},
	errorColors: {
		dark: {
			value: "#940524"},
		main: {
			value: "#BF072F"},
		light: {
			value: "#FBD0D9"}
	},
	warningColors: {
		dark: {
			value: "#994000"},
		main: {
			value: "#E9830C"},
		light: {
			value: "#FEE1CD"}
	},
	infoColors: {
		dark: {
			value: "#0F298A"},
		main: {
			value: "#0C3BE9"},
		light: {
			value: "#D5DCF6"}
	},
	successColors: {
		dark: {
			value: "#0A5C56"},
		main: {
			value: "#089188"},
		light: {
			value: "#BDF4F0"}
	},
	surfaceColors: {
		purple: {
			value: "#F2EDF8"},
		green: {
			value: "#E3F8F6"},
		yellow: {
			value: "#FDF9E2"},
		pink: {
			value: "#FDEDF0"},
		orange: {
			value: "#FFF1EB"},
		blue: {
			value: "#EDF0FD"},
		grey: {
			value: "#1A1A1A0A"}
	},
	textColors: {
		primary: {
			value: "#1A1A1A"},
		secondary: {
			value: "#5F5F5F"},
		disabled: {
			value: "#A3A3A3"}
	},
	graphColors: {
		purple10: {
			value: "#16082B"},
		purple20: {
			value: "#2C0F57"},
		purple30: {
			value: "#411782"},
		purple40: {
			value: "#6222C3"},
		purple50: {
			value: "#732FDB"},
		purple60: {
			value: "#8A51E1"},
		purple70: {
			value: "#A77DE8"},
		purple80: {
			value: "#C5A8F0"},
		purple90: {
			value: "#E7D9FC"},
		green10: {
			value: "#052E2B"},
		green20: {
			value: "#0A5C56"},
		green30: {
			value: "#089188"},
		green40: {
			value: "#0BC1B5"},
		green50: {
			value: "#1AE5D8"},
		green60: {
			value: "#47EBE0"},
		green70: {
			value: "#75F0E8"},
		green80: {
			value: "#9EFAF4"},
		green90: {
			value: "#BDF4F0"},
		yellow40: {
			value: "#CC9F00"},
		yellow50: {
			value: "#FFC700"},
		yellow60: {
			value: "#FFE533"},
		yellow70: {
			value: "#FFEB66"},
		yellow80: {
			value: "#FFF5B2"},
		yellow90: {
			value: "#FFF8CC"},
		grey10: {
			value: "#1A1A1A"},
		grey20: {
			value: "#333333"},
		grey30: {
			value: "#4D4D4D"},
		grey40: {
			value: "#666666"},
		grey50: {
			value: "#808080"},
		grey60: {
			value: "#999999"},
		grey70: {
			value: "#B3B3B3"},
		grey80: {
			value: "#CCCCCC"},
		grey90: {
			value: "#E6E6E6"},
		grey100: {
			value: "#FFFFFF"},
		red10: {
			value: "#31020C"},
		red20: {
			value: "#620418"},
		red30: {
			value: "#940524"},
		red40: {
			value: "#BF072F"},
		red50: {
			value: "#F6093D"},
		red60: {
			value: "#F83A63"},
		red70: {
			value: "#FA6B8A"},
		red80: {
			value: "#FB9DB1"},
		red90: {
			value: "#FBD0D9"},
		orange10: {
			value: "#2C1902"},
		orange20: {
			value: "#613705"},
		orange30: {
			value: "#915208"},
		orange40: {
			value: "#C26D0A"},
		orange50: {
			value: "#E9830C"},
		orange60: {
			value: "#F49C34"},
		orange70: {
			value: "#F7B364"},
		orange80: {
			value: "#F9CB94"},
		orange90: {
			value: "#FEE1CD"},
		blue10: {
			value: "#030C30"},
		blue20: {
			value: "#051961"},
		blue30: {
			value: "#082591"},
		blue40: {
			value: "#0A31C2"},
		blue50: {
			value: "#0C3BE9"},
		blue60: {
			value: "#345DF4"},
		blue70: {
			value: "#6483F7"},
		blue80: {
			value: "#94AAF9"},
		blue90: {
			value: "#D5DCF6"}
	}
};

const { primary, secondary, accent, graphColors: graph, neutralColors: neutral, stateColors: state, errorColors: error, warningColors: warning, infoColors: info, successColors: success, surfaceColors: surface, textColors: text, } = palette$1;
const primaryColors = {
    dark: primary.dark.value,
    main: primary.main.value,
    light: primary.light.value,
};
const secondaryColors = {
    dark: secondary.dark.value,
    main: secondary.main.value,
    light: secondary.light.value,
};
const accentColors = {
    dark: accent.dark.value,
    main: accent.main.value,
    light: accent.light.value,
};
const neutralColors = {
    main: neutral.main.value,
    light: neutral.light.value,
    disabled: neutral.disabled.value,
    divider: neutral.divider.value,
};
const stateColors = {
    outlinedChipHover: state.outlinedChipHover.value,
    filledChipHover: state.filledChipHover.value,
    outlinedButtonHover: state.outlinedButtonHover.value,
    tableRowSelected: state.tableRowSelected.value,
    tableRowHover: state.tableRowHover.value,
    tabSelected: state.tabSelected.value,
    tabHover: state.outlinedButtonHover.value,
};
const errorColors = {
    dark: error.dark.value,
    main: error.main.value,
    light: error.light.value,
};
const warningColors = {
    dark: warning.dark.value,
    main: warning.main.value,
    light: warning.light.value,
};
const infoColors = {
    dark: info.dark.value,
    main: info.main.value,
    light: info.light.value,
};
const successColors = {
    dark: success.dark.value,
    main: success.main.value,
    light: success.light.value,
};
const surfaceColors = {
    purple: surface.purple.value,
    green: surface.green.value,
    yellow: surface.yellow.value,
    pink: surface.pink.value,
    orange: surface.orange.value,
    blue: surface.blue.value,
    grey: surface.grey.value,
};
const textColors = {
    primary: text.primary.value,
    secondary: text.secondary.value,
    disabled: text.disabled.value,
};
const graphColors = {
    purple10: graph.purple10.value,
    purple20: graph.purple20.value,
    purple30: graph.purple30.value,
    purple40: graph.purple40.value,
    purple50: graph.purple50.value,
    purple60: graph.purple60.value,
    purple70: graph.purple70.value,
    purple80: graph.purple80.value,
    purple90: graph.purple90.value,
    green10: graph.green10.value,
    green20: graph.green20.value,
    green30: graph.green30.value,
    green40: graph.green40.value,
    green50: graph.green50.value,
    green60: graph.green60.value,
    green70: graph.green70.value,
    green80: graph.green80.value,
    green90: graph.green90.value,
    yellow40: graph.yellow40.value,
    yellow50: graph.yellow50.value,
    yellow60: graph.yellow60.value,
    yellow70: graph.yellow70.value,
    yellow80: graph.yellow80.value,
    yellow90: graph.yellow90.value,
    grey10: graph.grey10.value,
    grey20: graph.grey20.value,
    grey30: graph.grey30.value,
    grey40: graph.grey40.value,
    grey50: graph.grey50.value,
    grey60: graph.grey60.value,
    grey70: graph.grey70.value,
    grey80: graph.grey80.value,
    grey90: graph.grey90.value,
    grey100: graph.grey100.value,
    red10: graph.red10.value,
    red20: graph.red20.value,
    red30: graph.red30.value,
    red40: graph.red40.value,
    red50: graph.red50.value,
    red60: graph.red60.value,
    red70: graph.red70.value,
    red80: graph.red80.value,
    red90: graph.red90.value,
    orange10: graph.orange10.value,
    orange20: graph.orange20.value,
    orange30: graph.orange30.value,
    orange40: graph.orange40.value,
    orange50: graph.orange50.value,
    orange60: graph.orange60.value,
    orange70: graph.orange70.value,
    orange80: graph.orange80.value,
    orange90: graph.orange90.value,
    blue10: graph.blue10.value,
    blue20: graph.blue20.value,
    blue30: graph.blue30.value,
    blue40: graph.blue40.value,
    blue50: graph.blue50.value,
    blue60: graph.blue60.value,
    blue70: graph.blue70.value,
    blue80: graph.blue80.value,
    blue90: graph.blue90.value,
};

var calculateRemFromPx = (pxValue) => {
    if (!Number.isInteger(pxValue))
        throw new Error(`pxValue must be a number`);
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${(1 / 16) * pxValue}rem`;
};

const headingOptions = {
    fontFamily: 'Raleway',
    fontFeatureSettings: '"lnum", "pnum"',
    fontVariantLigatures: 'no-common-ligatures',
    fontWeight: 700,
    lineHeight: 1.4,
};
const typographyOptions = {
    typography: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        display: {
            ...headingOptions,
            fontWeight: 800,
            fontSize: calculateRemFromPx(64),
            lineHeight: 1.2,
        },
        h1: {
            ...headingOptions,
            fontSize: calculateRemFromPx(52),
            lineHeight: 1.3,
        },
        h2: {
            ...headingOptions,
            fontSize: calculateRemFromPx(40),
        },
        h3: {
            ...headingOptions,
            fontSize: calculateRemFromPx(32),
        },
        h4: {
            ...headingOptions,
            fontSize: calculateRemFromPx(28),
        },
        h5: {
            ...headingOptions,
            fontSize: calculateRemFromPx(24),
        },
        h6: {
            ...headingOptions,
            fontSize: calculateRemFromPx(20),
        },
        body1: {
            fontWeight: 400,
            fontSize: calculateRemFromPx(16),
            lineHeight: 1.5,
        },
        body2: {
            fontWeight: 400,
            fontSize: calculateRemFromPx(14),
            lineHeight: 1.43,
        },
        subtitle1: {
            fontWeight: 500,
            fontSize: calculateRemFromPx(16),
            lineHeight: 1.5,
        },
        subtitle2: {
            fontWeight: 500,
            fontSize: calculateRemFromPx(14),
            lineHeight: 1.43,
        },
        preamble: {
            //  Specify font family due to MUI not styling custom variants
            fontFamily: 'Open Sans',
            fontWeight: 400,
            fontSize: calculateRemFromPx(22),
            lineHeight: 1.5,
        },
        caption: {
            fontWeight: 400,
            fontSize: calculateRemFromPx(12),
            lineHeight: 1.4,
        },
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    display: 'display',
                    preamble: 'p',
                },
            },
            styleOverrides: {
                gutterBottom: {
                    marginBottom: '24px',
                },
            },
        },
    },
};
const typographyVariants = [
    'body1',
    'body2',
    'display',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'caption',
    'subtitle1',
    'subtitle2',
    'preamble',
];

const ReactRouterAdapterLink = forwardRef((props, ref) => {
    const { href, ...other } = props;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (href?.startsWith('/')) {
        // Map MUI's 'href' prop to react-router's 'to' if link is internal
        return jsx(Link, { ref: ref, to: href, ...other });
    }
    // else use a href with passed props
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return jsx("a", { ref: ref, href: href, ...other });
});

const ActionBar = (props) => {
    const { onAccept, onCancel } = props;
    return (jsxs(DialogActions, { children: [jsx(Button, { variant: "text", onClick: () => {
                    onCancel();
                }, children: "Avbryt" }), jsx(Button, { variant: "text", onClick: () => {
                    onAccept();
                }, children: "Ok" })] }));
};

const MuiButton = {
    components: {
        MuiButtonBase: {
            defaultProps: {
                LinkComponent: ReactRouterAdapterLink,
            },
        },
        MuiButton: {
            defaultProps: {
                variant: 'contained',
                size: 'medium',
                disableFocusRipple: true,
            },
            styleOverrides: {
                root: {
                    display: 'inline-flex',
                    minWidth: 'max-content',
                    borderRadius: 24,
                    textTransform: 'none',
                    lineHeight: '24px',
                    padding: '12px 24px',
                    boxShadow: 'none',
                    '&:hover, &:focus': {
                        boxShadow: 'none',
                    },
                    '&:focus-visible': {
                        outline: 'solid',
                        outlineColor: primaryColors.main,
                    },
                },
                contained: {
                    '&:focus': {
                        backgroundColor: primaryColors.dark,
                    },
                    '&:focus-visible': {
                        outlineOffset: '2px',
                    },
                },
                outlined: {
                    '&:focus': {
                        backgroundColor: stateColors.outlinedButtonHover,
                        border: `1px solid ${primaryColors.main}`,
                    },
                    '&:focus-visible': {
                        outlineOffset: '-2px',
                    },
                },
                text: {
                    '&:focus': {
                        backgroundColor: stateColors.outlinedButtonHover,
                    },
                },
                sizeSmall: {
                    fontSize: '0.875rem',
                    padding: '6px 12px',
                },
            },
        },
    },
};

const MuiAlert = {
    components: {
        MuiAlertTitle: {
            defaultProps: {
                variant: 'subtitle1',
                sx: {
                    mb: 1,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    '& .MuiAlert-message': {
                        paddingTop: '10px',
                    },
                },
            },
            variants: [
                {
                    props: { severity: 'info' },
                    style: {
                        backgroundColor: surfaceColors.blue,
                        color: infoColors.dark,
                    },
                },
                {
                    props: { severity: 'error' },
                    style: {
                        backgroundColor: surfaceColors.pink,
                        color: errorColors.dark,
                    },
                },
                {
                    props: { severity: 'success' },
                    style: {
                        backgroundColor: surfaceColors.green,
                        color: successColors.dark,
                    },
                },
                {
                    props: { severity: 'warning' },
                    style: {
                        backgroundColor: surfaceColors.orange,
                        color: warningColors.dark,
                    },
                },
            ],
            defaultProps: {
                iconMapping: {
                    error: createElement(ErrorRounded),
                    info: createElement(InfoRounded),
                    success: createElement(CheckCircleRounded),
                    warning: createElement(WarningRounded),
                },
            },
        },
    },
};

const MuiSnackbar = {
    components: {
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    '.MuiSnackbarContent-root': {
                        alignItems: 'start',
                    },
                    '.MuiSnackbarContent-message': {
                        flex: 1,
                        alignSelf: 'center',
                    },
                    '.MuiSnackbarContent-action': {
                        padding: '8px 0',
                        marginLeft: '24px',
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'error' },
                    style: {
                        '& .MuiSnackbarContent-root': {
                            backgroundColor: errorColors.main,
                            color: 'white',
                        },
                    },
                },
            ],
        },
    },
};

const MuiStepIcon = {
    components: {
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    '&.Mui-completed': {
                        color: secondaryColors.main,
                    },
                },
            },
        },
    },
};

const MuiIconButton = {
    components: {
        MuiIconButton: {
            defaultProps: {
                color: 'neutral',
            },
        },
    },
};

const MuiIcon = {
    components: {
        MuiIcon: {
            variants: [
                {
                    props: { fontSize: 'small' },
                    style: { fontSize: '20px' },
                },
                {
                    props: { fontSize: 'medium' },
                    style: { fontSize: '24px' },
                },
                {
                    props: { fontSize: 'large' },
                    style: { fontSize: '40px' },
                },
            ],
        },
        MuiSvgIcon: {
            variants: [
                {
                    props: { fontSize: 'small' },
                    style: { fontSize: '20px' },
                },
                {
                    props: { fontSize: 'medium' },
                    style: { fontSize: '24px' },
                },
                {
                    props: { fontSize: 'large' },
                    style: { fontSize: '40px' },
                },
            ],
        },
    },
};

const MuiDialogContentText = {
    components: {
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    color: textColors.primary,
                },
            },
        },
    },
};

const MuiAutoComplete = {
    components: {
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                        ':has(.MuiChip-root)': {
                            paddingTop: calculateRemFromPx(14),
                        },
                    },
                },
            },
        },
    },
};

const MuiRadio = {
    components: {
        MuiRadio: {
            defaultProps: {
                color: 'secondary',
            },
        },
    },
};

const MuiCheckbox = {
    components: {
        MuiCheckbox: {
            defaultProps: {
                color: 'secondary',
            },
        },
    },
};

const MuiSwitch = {
    components: {
        MuiSwitch: {
            defaultProps: {
                color: 'secondary',
            },
        },
    },
};

const generateStatusComponentStyling = (statusColor) => {
    const statusComponentStyle = {
        border: 'none',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        '&::before': {
            content: '""',
            backgroundColor: statusColor,
            height: '12.83px',
            width: '12.83px',
            borderRadius: '50%',
        },
    };
    return statusComponentStyle;
};

const MuiChip = {
    components: {
        MuiChip: {
            defaultProps: {
                variant: 'outlined',
                deleteIcon: createElement(Clear),
            },
            styleOverrides: {
                outlined: {
                    color: neutralColors.main,
                    '& .MuiChip-deleteIcon': {
                        color: neutralColors.main,
                    },
                    '&.MuiButtonBase-root': {
                        '&.MuiChip-clickable:focus': {
                            backgroundColor: stateColors.outlinedChipHover,
                        },
                    },
                },
                filled: {
                    backgroundColor: secondaryColors.light,
                    color: secondaryColors.dark,
                    '& .MuiSvgIcon-root': {
                        color: secondaryColors.dark,
                    },
                    '& .MuiChip-deleteIcon': {
                        '&:hover': {
                            color: secondaryColors.main,
                        },
                        fontSize: '1.375rem',
                    },
                    '&.MuiButtonBase-root': {
                        '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus': {
                            backgroundColor: stateColors.filledChipHover,
                        },
                    },
                },
                root: {
                    fontSize: '0.875rem',
                    lineHeight: '20px',
                    fontWeight: 500,
                    width: 'max-content',
                },
            },
            variants: [
                {
                    props: { variant: 'status-success' },
                    style: generateStatusComponentStyling(successColors.main),
                },
                {
                    props: { variant: 'status-info' },
                    style: generateStatusComponentStyling(infoColors.main),
                },
                {
                    props: { variant: 'status-error' },
                    style: generateStatusComponentStyling(errorColors.main),
                },
                {
                    props: { variant: 'status-warning' },
                    style: generateStatusComponentStyling(warningColors.main),
                },
            ],
        },
    },
};

const MuiTabs = {
    components: {
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    height: '56px',
                    borderRadius: '24px 24px 0 0',
                    padding: '0px 56px',
                    '&.Mui-selected': {
                        backgroundColor: stateColors.tabSelected,
                        '&:hover': {
                            backgroundColor: stateColors.tabSelected,
                        },
                    },
                    '&:hover': {
                        backgroundColor: stateColors.tabHover,
                    },
                },
            },
        },
        MuiTabs: {
            defaultProps: {
                size: 'medium',
                TabIndicatorProps: {
                    hidden: true,
                },
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    // tabs are always small in tablet and down
                    '& .MuiTab-root': {
                        [theme.breakpoints.down('md')]: {
                            fontSize: '14px',
                            height: '44px',
                            minHeight: '44px',
                            borderRadius: '16px 16px 0 0',
                            padding: '12px 24px',
                        },
                    },
                    // Always allow the scroll buttons to be visible on scrollable tab groups.
                    // Default is opacity 0.
                    // https://mui.com/material-ui/react-tabs/#forced-scroll-buttons
                    '& .MuiTabs-scrollButtons.Mui-disabled': {
                        opacity: 0.3,
                    },
                }),
            },
            variants: [
                {
                    props: { size: 'small' },
                    style: {
                        '& .MuiTab-root': {
                            fontSize: '14px',
                            height: '44px',
                            // Override min height otherwise 'small' tabs will be 48px high
                            minHeight: '44px',
                            borderRadius: '16px 16px 0 0',
                            padding: '12px 24px',
                        },
                    },
                },
            ],
        },
    },
};

const MuiAvatar = {
    components: {
        MuiAvatar: {
            defaultProps: {
                sx: {
                    width: '40px',
                    height: '40px',
                },
            },
        },
    },
};

const MuiCircularProgress = {
    components: {
        MuiCircularProgress: {
            defaultProps: {
                size: 32,
            },
        },
    },
};

const MuiLinearProgress = {
    components: {
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    width: '100%',
                },
            },
        },
    },
};

const MuiListItem = {
    components: {
        MuiListItem: {
            defaultProps: {
                disablePadding: true,
            },
        },
        MuiListItemText: {
            styleOverrides: {
                secondary: {
                    fontSize: '0.875rem',
                },
            },
        },
    },
};

const MuiCard = {
    components: {
        MuiCard: {
            defaultProps: {
                variant: 'outlined',
            },
            styleOverrides: {
                root: {
                    borderRadius: '16px',
                    '& a:focus-visible': {
                        outline: 'solid',
                        outlineColor: primaryColors.main,
                        outlineOffset: '-2px',
                    },
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '24px 24px 0px 24px',
                    // if header sub component is in use, then we want
                    // the card content to have less top padding. 12px instead of 24px.
                    '& + .MuiCardContent-root': {
                        paddingTop: '12px',
                    },
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '24px',
                    // For now will not work with Firefox. As of 2023-09-16, the :has selector is in development.
                    // This is accepted as it's such a minor difference in style.
                    '&:has(+ div.MuiCardActions-root)': {
                        paddingBottom: '12px',
                    },
                },
            },
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '0px 16px 16px 12px',
                },
            },
        },
    },
};

const MuiLink = {
    components: {
        MuiLink: {
            defaultProps: {
                underline: 'none',
                component: ReactRouterAdapterLink,
                // Limitations in MUI typing requires casting to LinkProps to use 3rd party links
            },
        },
    },
};

const MuiPagination = {
    components: {
        MuiPagination: {
            defaultProps: {
                color: 'primary',
            },
        },
    },
};

const MuiMenuItem = {
    components: {
        MuiMenuItem: {
            defaultProps: {
                sx: {
                    paddingY: '16px',
                },
            },
        },
    },
};

const MuiFormHelperText = {
    components: {
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                },
            },
        },
    },
};

const MuiTextField = {
    components: {
        MuiTextField: {
            styleOverrides: {
                root: {
                    svg: {
                        color: textColors.secondary,
                    },
                    '& .MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.875)',
                    },
                    '& fieldset > legend': {
                        fontSize: '0.875rem',
                    },
                },
            },
        },
    },
};

const MuiInputAdornment = {
    components: {
        MuiInputAdornment: {
            styleOverrides: {
                positionStart: {
                    paddingRight: '16px',
                    margin: 0,
                },
            },
        },
    },
};

const MuiInputLabel = {
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.875)',
                    },
                },
            },
        },
    },
};

const MuiSelect = {
    components: {
        MuiSelect: {
            styleOverrides: {
                root: {
                    '& fieldset > legend': {
                        fontSize: '0.875rem',
                    },
                },
            },
        },
    },
};

const MuiTableRow = {
    components: {
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        backgroundColor: stateColors.tableRowSelected,
                        '&:hover': {
                            backgroundColor: stateColors.tableRowHover,
                        },
                    },
                },
            },
        },
    },
};

const MuiDataGrid = {
    components: {
        MuiDataGrid: {
            defaultProps: {
                slotProps: {
                    baseButton: {
                        variant: 'text',
                    },
                },
            },
            styleOverrides: {
                root: {
                    '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                        color: neutralColors.main,
                    },
                    '& .MuiDataGrid-row.Mui-selected': {
                        backgroundColor: stateColors.tableRowSelected,
                        '&:hover': {
                            backgroundColor: stateColors.tableRowHover,
                        },
                    },
                },
                // Wrapper for both 'Columns' and 'Filter' menus
                panelWrapper: {
                    padding: '8px',
                },
                columnsManagementRow: {
                    padding: '40px',
                    margin: '50px',
                    '&:not(:last-child)': {
                        paddingBottom: '16px',
                    },
                },
                // Footer for 'Columns' menu
                panelFooter: {
                    justifyContent: 'end',
                },
                // Wrapper for 'Density' and 'Export' menus
                menu: {
                    padding: '8px',
                },
            },
        },
    },
};

const defaultDateFormat = 'yyyy-MM-dd';
const MuiDatePicker = {
    components: {
        MuiDatePicker: {
            defaultProps: {
                disableHighlightToday: true,
                format: defaultDateFormat,
            },
        },
        MuiDesktopDatePicker: {
            defaultProps: {
                format: defaultDateFormat,
            },
        },
        MuiMobileDatePicker: {
            defaultProps: {
                disableHighlightToday: true,
                label: 'Välj datum',
                format: defaultDateFormat,
                slots: {
                    actionBar: ActionBar,
                },
            },
        },
    },
};

const MuiTimePicker = {
    components: {
        MuiTimePicker: {
            defaultProps: {
                label: 'Välj tid',
            },
        },
    },
};

const MuiToggleButton = {
    components: {
        MuiToggleButtonGroup: {
            defaultProps: {
                size: 'medium',
                color: 'secondary',
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.spacing(3),
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: neutralColors.divider,
                    overflow: 'hidden',
                }),
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    return {
                        textTransform: 'initial',
                        padding: ownerState.size === 'small'
                            ? theme.spacing(0, 2.25) // ≈ 0 18 px
                            : theme.spacing(0, 3), // ≈ 0 24 px
                        minHeight: ownerState.size === 'small'
                            ? theme.spacing(5) // ≈ 40 px
                            : theme.spacing(5.75), // ≈ 46 px
                        fontSize: ownerState.size === 'small' ? 12 : undefined,
                        border: 0,
                        color: textColors.secondary,
                        ...(!ownerState.className?.includes('MuiToggleButtonGroup-groupedVertical') && {
                            [`&:not(:last-of-type)`]: {
                                borderRight: `1px solid ${neutralColors.divider}`,
                            },
                        }),
                        ...(ownerState.className?.includes('MuiToggleButtonGroup-groupedVertical') && {
                            [`&:not(:last-of-type)`]: {
                                borderBottom: `1px solid ${neutralColors.divider}`,
                            },
                        }),
                    };
                },
            },
            variants: [
                {
                    props: { color: 'secondary' },
                    style: {
                        '&.Mui-selected': {
                            backgroundColor: secondaryColors.light,
                            color: textColors.secondary,
                        },
                        '&.Mui-selected:hover': {
                            backgroundColor: secondaryColors.main,
                            color: secondaryColors.light,
                        },
                    },
                },
            ],
        },
    },
};

const MuiOutlinedInput = {
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },
    },
};

var themeComponents = {
    ...MuiButton.components,
    ...MuiAlert.components,
    ...MuiSnackbar.components,
    ...MuiStepIcon.components,
    ...MuiIconButton.components,
    ...MuiIcon.components,
    ...MuiDialogContentText.components,
    ...MuiAutoComplete.components,
    ...MuiRadio.components,
    ...MuiCheckbox.components,
    ...MuiSwitch.components,
    ...MuiChip.components,
    ...MuiTabs.components,
    ...MuiAvatar.components,
    ...MuiCircularProgress.components,
    ...MuiLinearProgress.components,
    ...MuiListItem.components,
    ...MuiCard.components,
    ...MuiLink.components,
    ...MuiPagination.components,
    ...MuiMenuItem.components,
    ...MuiFormHelperText.components,
    ...MuiTextField.components,
    ...MuiInputAdornment.components,
    ...MuiInputLabel.components,
    ...MuiSelect.components,
    ...MuiTableRow.components,
    ...MuiDataGrid.components,
    ...MuiDatePicker.components,
    ...MuiTimePicker.components,
    ...MuiToggleButton.components,
    ...MuiOutlinedInput.components,
};

const responsiveFontSizeOptions = {
    variants: typographyVariants,
};
const palette = {
    primary: primaryColors,
    secondary: secondaryColors,
    accent: accentColors,
    neutral: neutralColors,
    error: errorColors,
    warning: warningColors,
    info: infoColors,
    success: successColors,
    surface: surfaceColors,
    graph: graphColors,
    text: textColors,
    stateColors: stateColors,
};
const themeOptions = {
    palette: {
        ...palette,
    },
    ...typographyOptions,
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1920,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: baseLineCss,
        },
        ...typographyOptions.components,
        ...themeComponents,
    },
};
const defaultLocale = svSE;

export { defaultLocale, palette, responsiveFontSizeOptions, themeOptions };
//# sourceMappingURL=index.esm.js.map
